import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import FieldLayout, { FieldErrors } from "components/field/FieldLayout";
import { BatteryBanner, BatteryCard, usePower } from "components/field/BatteryRunway";
import SatellitePasses from "components/field/SatellitePasses";
import InsarGroundTruth from "components/field/InsarGroundTruth";
import AdvancedPanel from "components/field/node/AdvancedPanel";
import CaptureNow from "components/field/node/CaptureNow";
import LatestPhotos from "components/field/node/LatestPhotos";
import NodeStatusCard from "components/field/node/NodeStatusCard";
import ScheduleEditor from "components/field/node/ScheduleEditor";
import WaterLevelCard from "components/field/node/WaterLevelCard";
import RadarHeatmapCard from "components/field/node/RadarHeatmapCard";
import { useFieldNodes, useFieldResource } from "hooks/useFieldNodes";
import { rtctApi } from "services/rtctApi";
import { timeOf } from "utils/radarDiagnostics";

// One page per field node (/node/:mac): status, schedule, capture now, water
// level, photos, satellite passes and the way to the tool pages. It reads
// what the four field pages read and writes only through the settings
// endpoint (applySettings), each write behind its own button; live mode,
// calibration campaigns and the Radar lab stay on their pages. Times are
// node-local: UTC+8 (SGT), as the field pages show them.
export const NODE_TZ_HOURS = 8;

// The node in the URL is its slug (the address without colons, as the API accepts it): /node/020000000081.
const bare = (v) => String(v || "").replaceAll(":", "").toUpperCase();
export const slugOf = (devices, mac) => devices.find((d) => d.mac === mac)?.mac_slug || bare(mac);
export const macOf = (devices, wanted) =>
  devices.find((d) => d.mac === wanted || d.mac_slug === wanted || bare(d.mac) === bare(wanted))?.mac || null;

export default function NodePage() {
  const { mac: wanted } = useParams();
  const navigate = useNavigate();
  const nodes = useFieldNodes();
  const { devices, mac, select } = nodes;
  // The node in the URL wins once the node list has it; /node alone shows the node chosen last.
  const target = wanted ? macOf(devices, wanted) : null;
  useEffect(() => {
    if (target && target !== mac) select(target);
  }, [target, mac, select]);
  useEffect(() => {
    if (!wanted && mac) navigate(`/node/${slugOf(devices, mac)}`, { replace: true });
  }, [wanted, mac, devices, navigate]);
  const view = useMemo(
    () => ({ ...nodes, select: (m) => navigate(`/node/${slugOf(devices, m)}`) }),
    [nodes, devices, navigate],
  );
  return <NodePageContent key={mac} nodes={view} />;
}

function NodePageContent({ nodes }) {
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") setRefresh((n) => n + 1);
    }, 60000);
    return () => clearInterval(timer);
  }, []);
  const telemetry = useFieldResource(nodes.mac, rtctApi.telemetry, refresh);
  const settings = useFieldResource(nodes.mac, rtctApi.settings, refresh);
  // Battery runway (read-only, its own 5-minute poll): the banner while a warning is active, the card under the status.
  const power = usePower(nodes.mac);
  // A settings answer from this page's own Apply, until the next poll brings a newer one.
  const [applied, setApplied] = useState(null);
  const shownSettings = applied || settings.data;
  useEffect(() => setApplied(null), [settings.data]);
  const sample = useMemo(
    () =>
      (telemetry.data?.samples || []).reduce(
        (best, s) => ((timeOf(s?.timestamp) ?? -Infinity) > (timeOf(best?.timestamp) ?? -Infinity) ? s : best),
        null,
      ),
    [telemetry.data],
  );
  return (
    <FieldLayout
      nodes={nodes}
      title={nodes.node?.name || "Node"}
      section="node"
      refresh={() => setRefresh((n) => n + 1)}
    >
      <FieldErrors errors={[telemetry.error, settings.error]} />
      {nodes.mac && <BatteryBanner power={power} />}
      {nodes.mac ? (
        <div className="field-control-grid">
          <div className="field-control-column">
            <NodeStatusCard sample={sample} />
            <BatteryCard power={power} />
            <ScheduleEditor
              key={`${nodes.mac}-${shownSettings?.request_id || ""}`}
              mac={nodes.mac}
              settings={shownSettings}
              sample={sample}
              tzHours={NODE_TZ_HOURS}
              onApplied={setApplied}
            />
            <CaptureNow mac={nodes.mac} settings={shownSettings} sample={sample} onApplied={setApplied} />
          </div>
          <div className="field-control-column">
            <WaterLevelCard mac={nodes.mac} />
            <RadarHeatmapCard mac={nodes.mac} />
            <LatestPhotos mac={nodes.mac} />
            <SatellitePasses mac={nodes.mac} />
            <InsarGroundTruth mac={nodes.mac} />
            <AdvancedPanel settings={shownSettings} />
          </div>
        </div>
      ) : (
        <div className="field-empty">
          <h2>No field node yet</h2>
          <p>Nodes appear here once they report to this server.</p>
        </div>
      )}
    </FieldLayout>
  );
}
